import axios from 'axios';
// local-search plugin disabled, so avoid GraphQL static queries
import React, { useContext, useEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';

import { SearchModalContext } from '../../contexts/searchModalContext';
import { SearchModalStyles } from '../../styles/search/SearchModalStyles';
import ActionButton from '../buttons/ActionButton';
import SearchResult from './SearchResult';
import { BlogSearchResultItem } from './SearchResultItem';
import SearchField from './SearchField';
import ParagraphText from '../typography/ParagraphText';
import { getFatawaSlug, normalizeFatawa } from '../../utils/fatawa';

function Search() {
  const { isSearchModalOpen, searchQuery, setSearchQuery, closeSearchModal } =
    useContext(SearchModalContext);
  const [blogsIndexStore, setBlogsIndexStore] = useState(null);
  const [categoriesIndexStore, setCategoriesIndexStore] = useState(null);
  const [authorsIndexStore, setAuthorsIndexStore] = useState(null);
  const [fallbackBlogs, setFallbackBlogs] = useState(null);
  const [isFallbackLoading, setIsFallbackLoading] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isSearchModalOpen) {
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    } else {
      document.body.style.overflow = 'initial';
    }
  }, [isSearchModalOpen]);

  // Safely read local search JSON URLs (may be null if plugin data isn't available)
  const blogsPublicStoreURL = '';
  const blogsPublicIndexURL = '';
  const categoriesPublicStoreURL = '';
  const categoriesPublicIndexURL = '';
  const authorsPublicStoreURL = '';
  const authorsPublicIndexURL = '';

  const handleOnFocus = async () => {
    if (blogsIndexStore && categoriesIndexStore && authorsIndexStore) return;
    // if any URL is missing, skip fetching (plugin may not have generated local JSON)
    if (
      !blogsPublicIndexURL ||
      !blogsPublicStoreURL ||
      !categoriesPublicIndexURL ||
      !categoriesPublicStoreURL ||
      !authorsPublicIndexURL ||
      !authorsPublicStoreURL
    ) {
      // nothing to load
      // you might want to populate search indices from pageContext or a remote API instead
      // console.warn('Search JSON URLs missing, skipping fetch');
      return;
    }
    const [
      { data: blogsIndex },
      { data: blogsStore },
      { data: categoriesIndex },
      { data: categoriesStore },
      { data: authorsIndex },
      { data: authorsStore },
    ] = await Promise.all([
      axios.get(`${blogsPublicIndexURL}`),
      axios.get(`${blogsPublicStoreURL}`),
      axios.get(`${categoriesPublicIndexURL}`),
      axios.get(`${categoriesPublicStoreURL}`),
      axios.get(`${authorsPublicIndexURL}`),
      axios.get(`${authorsPublicStoreURL}`),
    ]);
    setBlogsIndexStore({
      index: blogsIndex,
      store: blogsStore,
    });
    setCategoriesIndexStore({
      index: categoriesIndex,
      store: categoriesStore,
    });
    setAuthorsIndexStore({
      index: authorsIndex,
      store: authorsStore,
    });
  };

  const shouldUseFallback =
    !blogsPublicIndexURL ||
    !blogsPublicStoreURL ||
    !categoriesPublicIndexURL ||
    !categoriesPublicStoreURL ||
    !authorsPublicIndexURL ||
    !authorsPublicStoreURL;

  useEffect(() => {
    if (!isSearchModalOpen) return;
    if (!shouldUseFallback) return;
    if (fallbackBlogs || isFallbackLoading) return;
    const API =
      process.env.GATSBY_FILAMENT_API_BASE ||
      process.env.FILAMENT_API_BASE ||
      'https://api.turaath.so';
    setIsFallbackLoading(true);
    axios
      .get(`${API}/fatawas`)
      .then(({ data: items }) => {
        const list = Array.isArray(items) ? items : [];
        const mapped = list.map((item) => {
          const normalized = normalizeFatawa(item, API);
          return {
            ...normalized,
            slug: { current: getFatawaSlug(normalized) },
          };
        });
        setFallbackBlogs(mapped);
      })
      .catch(() => setFallbackBlogs([]))
      .finally(() => setIsFallbackLoading(false));
  }, [isSearchModalOpen, shouldUseFallback, fallbackBlogs, isFallbackLoading]);

  if (!isSearchModalOpen) return null;
  const handleCloseKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeSearchModal();
    }
  };

  return (
    <SearchModalStyles>
      <div className="searchModalOverlay">
        <div className="modal__container">
          <ActionButton
            className="close"
            role="button"
            tabIndex={0}
            aria-label="Close search"
            onClick={() => closeSearchModal()}
            onKeyDown={handleCloseKeyDown}
          >
            <MdClose />
          </ActionButton>
          <SearchField
            value={searchQuery}
            setValue={setSearchQuery}
            onFocus={handleOnFocus}
            inputRef={searchInputRef}
          />
          {searchQuery &&
            blogsIndexStore &&
            categoriesIndexStore &&
            authorsIndexStore && (
              <div className="search__result">
                <SearchResult
                  searchQuery={searchQuery}
                  blogsIndexStore={blogsIndexStore}
                  categoriesIndexStore={categoriesIndexStore}
                  authorsIndexStore={authorsIndexStore}
                />
              </div>
            )}

          {searchQuery &&
            shouldUseFallback &&
            !isFallbackLoading &&
            fallbackBlogs && (
              <div className="search__result">
                {fallbackBlogs
                  .filter((item) =>
                    item.title
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase().trim())
                  )
                  .slice(0, 20)
                  .map((result) => (
                    <BlogSearchResultItem key={result.id} blog={result} />
                  ))}
                {fallbackBlogs.length === 0 && (
                  <ParagraphText>Wax natiijo ah lama helin.</ParagraphText>
                )}
              </div>
            )}
        </div>
      </div>
    </SearchModalStyles>
  );
}

export default Search;
